/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * usePullToRefresh - Pull-to-refresh gesture hook
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useRef, useCallback, useEffect } from 'react'

const PULL_THRESHOLD = 80 // Minimum pull distance to trigger refresh
const MAX_PULL_DISTANCE = 120 // Maximum visual pull distance
const RESISTANCE_FACTOR = 0.5 // Pull resistance for natural feel

/**
 * Hook to handle pull-to-refresh gesture
 *
 * @param {Object} options
 * @param {Function} options.onRefresh - Callback when refresh is triggered
 * @param {boolean} options.enabled - Whether pull-to-refresh is enabled
 * @param {number} options.threshold - Pull distance to trigger refresh
 * @returns {Object} Pull state and ref
 */
export const usePullToRefresh = ({
  onRefresh,
  enabled = true,
  threshold = PULL_THRESHOLD
}) => {
  const [pullDistance, setPullDistance] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isPulling, setIsPulling] = useState(false)

  const containerRef = useRef(null)
  const touchStartY = useRef(0)
  const scrollTopAtStart = useRef(0)

  // Check if we're at the top of scrollable content
  const isAtTop = useCallback(() => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    return scrollTop <= 0
  }, [])

  const handleTouchStart = useCallback(
    (e) => {
      if (!enabled || isRefreshing) return
      if (!isAtTop()) return

      touchStartY.current = e.touches[0].clientY
      scrollTopAtStart.current = window.scrollY
      setIsPulling(true)
    },
    [enabled, isRefreshing, isAtTop]
  )

  const handleTouchMove = useCallback(
    (e) => {
      if (!enabled || isRefreshing || !isPulling) return
      if (!isAtTop() && scrollTopAtStart.current > 0) {
        setIsPulling(false)
        setPullDistance(0)
        return
      }

      const touchY = e.touches[0].clientY
      const deltaY = touchY - touchStartY.current

      // Only allow pulling down
      if (deltaY > 0) {
        // Apply resistance for natural feel
        const resistedDistance = Math.min(
          deltaY * RESISTANCE_FACTOR,
          MAX_PULL_DISTANCE
        )
        setPullDistance(resistedDistance)

        // Prevent default scroll when pulling
        if (resistedDistance > 10) {
          e.preventDefault()
        }
      }
    },
    [enabled, isRefreshing, isPulling, isAtTop]
  )

  const handleTouchEnd = useCallback(async () => {
    if (!enabled || isRefreshing) return

    if (pullDistance >= threshold) {
      setIsRefreshing(true)
      setPullDistance(threshold) // Hold at threshold during refresh

      try {
        await onRefresh?.()
      } finally {
        setIsRefreshing(false)
        setPullDistance(0)
      }
    } else {
      setPullDistance(0)
    }

    setIsPulling(false)
  }, [enabled, isRefreshing, pullDistance, threshold, onRefresh])

  // Attach event listeners
  useEffect(() => {
    if (!enabled) return

    const container = containerRef.current
    if (!container) return

    container.addEventListener('touchstart', handleTouchStart, { passive: true })
    container.addEventListener('touchmove', handleTouchMove, { passive: false })
    container.addEventListener('touchend', handleTouchEnd, { passive: true })

    return () => {
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
      container.removeEventListener('touchend', handleTouchEnd)
    }
  }, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd])

  // Progress calculation (0 to 1)
  const progress = Math.min(pullDistance / threshold, 1)
  const isReadyToRefresh = pullDistance >= threshold

  return {
    containerRef,
    pullDistance,
    progress,
    isRefreshing,
    isPulling,
    isReadyToRefresh
  }
}

export default usePullToRefresh
